import { HTTPMethod } from '@perpay-web/constants/httpMethods';
import { GOOGLE_PLACES_KEY } from '@perpay-web/constants/serviceCredentials';
import { GOOGLE_AUTOCOMPLETE_URL } from '@perpay-web/constants/urls';
import { getData, useDataModule } from '@perpay-web/data-module/useDataModule';
import { createContextProvider } from '@perpay-web/utils/componentUtils';
import { jsonFetch } from '@perpay-web/utils/requestUtils';

import { useDebouncedDataModule } from './useDebouncedDataModule';

export const getAddressSuggestions = (state) => getData(state);

const DEBOUNCE_MS = 500;

const useAddressSuggestionsDataModule = () => {
    const dataModule = useDataModule(
        async (typeahead) => {
            if (!typeahead) {
                return [];
            }
            const url = new URL(GOOGLE_AUTOCOMPLETE_URL);

            const body = {
                input: typeahead,
                includedRegionCodes: ['us'],
                languageCode: 'en',
                includedPrimaryTypes: ['street_address', 'premise'],
            };

            const addressData = await jsonFetch(url, body, HTTPMethod.post, {
                'X-Goog-Api-Key': GOOGLE_PLACES_KEY,
            });

            const suggestions = addressData.suggestions || [];
            return suggestions.slice(0, 4).map(
                ({
                    placePrediction: {
                        placeId,
                        structuredFormat: {
                            mainText: { text: line1 },
                            secondaryText: { text: line2 },
                        },
                    },
                }) => ({
                    placeId,
                    line1,
                    line2,
                }),
            );
        },
        { initialValue: [] },
    );

    return useDebouncedDataModule(dataModule, DEBOUNCE_MS);
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const dataModule = useAddressSuggestionsDataModule();

        return (
            <Context.Provider value={dataModule}>{children}</Context.Provider>
        );
    };

    return Provider;
};

export const [
    UseAddressSuggestionsDataModuleProvider,
    useAddressSuggestionsContext,
] = createContextProvider(providerFactory);
